import { useState, useCallback, useRef } from 'react'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import api from '@cannect/services/api'

interface Flavor {
  id: number
  flavor: string
  mainProduct?: number
  balance?: number
  total?: number
  status?: string
}

interface FlavorManagerOptions {
  onFlavorChange?: (productId: number, flavorId: number, quantity?: number) => Promise<void>
}

interface UseFlavorManagerReturn {
  flavorsCache: Record<number, Flavor[]>
  loadingFlavors: Record<number, boolean>
  fetchProductFlavors: (productId: number) => Promise<Flavor[]>
  handleFlavorChange: (productId: number, flavorId: number, quantity?: number) => Promise<void>
  getProductFlavors: (productId: number) => Flavor[]
  clearCache: () => void
  preloadProductFlavors: (productId: number, mainProductId?: number) => Promise<Flavor[]>
  shouldShowFlavorButton: (productId: number) => boolean
  getFlavorCount: (productId: number) => number
  markProductAsNoFlavors: (productId: number) => void
}

export function useFlavorManager(options: FlavorManagerOptions = {}): UseFlavorManagerReturn {
  const { addToCartFlavor, getLastOrder } = usePrepareCart()
  const [flavorsCache, setFlavorsCache] = useState<Record<number, Flavor[]>>({})
  const [loadingFlavors, setLoadingFlavors] = useState<Record<number, boolean>>({})
  const [productsWithNoFlavors, setProductsWithNoFlavors] = useState<Set<number>>(new Set())
  
  const flavorsCacheRef = useRef(flavorsCache)
  const loadingFlavorsRef = useRef(loadingFlavors)
  const productsWithNoFlavorsRef = useRef(productsWithNoFlavors)
  
  flavorsCacheRef.current = flavorsCache
  loadingFlavorsRef.current = loadingFlavors
  productsWithNoFlavorsRef.current = productsWithNoFlavors

  const fetchProductFlavors = useCallback(async (productId: number): Promise<Flavor[]> => {
    console.log('fetchProductFlavors', productId)
    const currentCache = flavorsCacheRef.current[productId]
    if (currentCache) {
      return currentCache
    }

    const currentLoading = loadingFlavorsRef.current[productId]
    if (currentLoading) {
      return []
    }

    try {
      setLoadingFlavors(prev => ({ ...prev, [productId]: true }))
      
      const { data: flavorsData } = await api.get(`/get_product_flavors/${productId}`)
      
      const { data: productData } = await api.get(`/product/${productId}`)
      
      const allFlavors: Flavor[] = []
      const hasSiblingFlavors = !!flavorsData?.success && (flavorsData.productFlavors?.length || 0) > 0

      if (productData?.product) {
        const mainProduct = productData.product
        if (mainProduct.flavor || hasSiblingFlavors) {
          allFlavors.push({
            id: mainProduct.id,
            flavor: mainProduct.flavor || 'Natural',
            mainProduct: productId,
            balance: mainProduct.stock || 0,
            total: mainProduct.stock || 0,
            status: mainProduct.status || 'A'
          })
        }
      }

      if (flavorsData?.success && flavorsData.productFlavors?.length > 0) {
        flavorsData.productFlavors.forEach((product: any) => {
          if (product.flavor && product.flavor.id) {
            allFlavors.push({
              id: product.id,
              flavor: product.flavor.flavor || product.flavor.name || 'Sabor',
              mainProduct: productId,
              balance: product.stock || 0,
              total: product.stock || 0,
              status: product.status || 'A'
            })
          }
        })
      }

      if (allFlavors.length > 0) {
        setFlavorsCache(prev => ({ ...prev, [productId]: allFlavors }))
        return allFlavors
      } else {
        setProductsWithNoFlavors(prev => new Set(prev).add(productId))
        setFlavorsCache(prev => ({ ...prev, [productId]: [] }))
        return []
      }
    } catch (error) {
      console.error('Erro ao buscar sabores:', error)
    } finally {
      setLoadingFlavors(prev => ({ ...prev, [productId]: false }))
    }
    
    return []
  }, [])

  const handleFlavorChange = useCallback(async (productId: number, flavorId: number, quantity: number = 1) => {
    try {
      if (options.onFlavorChange) {
        await options.onFlavorChange(productId, flavorId, quantity)
      } else {
        await addToCartFlavor(productId, flavorId, quantity)
        await getLastOrder()
      }
    } catch (error) {
      console.error('Erro ao trocar sabor:', error)
      throw error
    }
  }, [options, addToCartFlavor, getLastOrder])

  const getProductFlavors = useCallback((productId: number): Flavor[] => {
    const mainProductId = productId
    return flavorsCacheRef.current[mainProductId] || []
  }, [])

  const preloadProductFlavors = useCallback(async (productId: number, mainProductId?: number): Promise<Flavor[]> => {
    const productIdToFetch = mainProductId || productId
    return await fetchProductFlavors(productIdToFetch)
  }, [fetchProductFlavors])

  const shouldShowFlavorButton = useCallback((productId: number): boolean => {
    const mainProductId = productId
    const cachedFlavors = flavorsCacheRef.current[mainProductId] || []
    const isLoading = loadingFlavorsRef.current[mainProductId]
    const hasNoFlavors = productsWithNoFlavorsRef.current.has(mainProductId)
    
    if (isLoading) return false
    
    if (hasNoFlavors) return false
    
    const availableFlavors = cachedFlavors.filter(f => f.flavor && f.flavor !== 'Sabor' && f.status === 'A')
    return availableFlavors.length > 1
  }, [])

  const getFlavorCount = useCallback((productId: number): number => {
    const mainProductId = productId
    const cachedFlavors = flavorsCacheRef.current[mainProductId] || []
    const availableFlavors = cachedFlavors.filter(f => f.flavor && f.flavor !== 'Sabor' && f.status === 'A')
    return availableFlavors.length
  }, [])

  const markProductAsNoFlavors = useCallback((productId: number) => {
    setProductsWithNoFlavors(prev => new Set(prev).add(productId))
  }, [])

  const clearCache = useCallback(() => {
    setFlavorsCache({})
    setLoadingFlavors({})
    setProductsWithNoFlavors(new Set())
  }, [])

  return {
    flavorsCache,
    loadingFlavors,
    fetchProductFlavors,
    handleFlavorChange,
    getProductFlavors,
    clearCache,
    preloadProductFlavors,
    shouldShowFlavorButton,
    getFlavorCount,
    markProductAsNoFlavors
  }
}
